/**
 * Laesst einen zu langen Produktnamen von selbst hin und her wandern.
 *
 * Der Name steht im Kauffenster und in den Vorschaufeldern auf einer Zeile. Passt er nicht, liesse er sich
 * zwar seitlich schieben - man muesste aber erst merken, dass da noch etwas
 * kommt. Also wandert er von allein, mit Pause an beiden Enden.
 *
 * Der Weg wird gemessen und als Variable gesetzt; die Bewegung selbst macht
 * CSS. Bei reduzierter Bewegung bleibt alles stehen, dann bleibt das Schieben
 * von Hand.
 *
 * Gemessen wird ausschliesslich im ResizeObserver. Dessen Meldungen kommen,
 * nachdem der Browser die Seite ohnehin angeordnet hat - eine Messung dort
 * kostet nichts. Frueher mass das Skript bei jeder Aenderung am Dokument
 * selbst nach und zwang den Browser dabei, die ganze Seite neu anzuordnen:
 * auf Android gemessen rund 350 ms waehrend des Ladens, genau in der Zeit,
 * in der das Kauffenster hereinfaehrt.
 */
const AUSWAHL = [
  '.dp-kauffenster .text-block :is(h1, h2, h3)',
  '.dp-product__panel-title',
  '.mega-menu__preview-title',
  // Bestand und Lieferzeit neben dem Warenkorb-Knopf: statt umzubrechen,
  // wandern sie, wenn das Fenster zu schmal wird.
  '.dp-lieferung__bestand',
  '.dp-lieferung__zeit',
].join(', ');

/** Legt die Huelle um den Text, falls sie fehlt - bewegt wird sie, nicht der Kasten. */
function huelleVon(element) {
  let huelle = element.querySelector(':scope > [data-dp-lauf]');
  if (!huelle) {
    huelle = document.createElement('span');
    huelle.dataset.dpLauf = '';
    /*
     * Als Blockelement in der Zeile - ein gewoehnliches inline-Element hat
     * keine eigene Breite, an der sich messen liesse.
     */
    huelle.style.display = 'inline-block';
    while (element.firstChild) huelle.appendChild(element.firstChild);
    element.appendChild(huelle);
  }
  return huelle;
}

/** @param {HTMLElement} element */
function pruefe(element) {
  const huelle = element.querySelector(':scope > [data-dp-lauf]');
  if (!huelle) return;
  const weg = Math.ceil(huelle.getBoundingClientRect().width - element.clientWidth);

  if (weg > 4) {
    element.style.setProperty('--dp-lauf-weg', '-' + weg + 'px');
    // Laenger Text, laengere Reise - etwa 45 Pixel je Sekunde.
    /*
     * Aendert sich die Breite des Kastens stetig (der Mengenteil neben dem Hinweis waechst
     * und schrumpft), meldet der Beobachter jedes Bild. Eine laufende Reise darf dabei nicht
     * mit neuer Dauer springen: erst bei einer spuerbaren Abweichung (3 s) wird sie neu gesetzt.
     */
    const dauer = Math.max(6, weg / 45 + 4);
    const alt = parseFloat(element.style.getPropertyValue('--dp-lauf-dauer'));
    if (!(alt > 0) || Math.abs(dauer - alt) >= 3) element.style.setProperty('--dp-lauf-dauer', dauer.toFixed(1) + 's');
    element.dataset.dpLaeuft = '';
  } else {
    delete element.dataset.dpLaeuft;
    element.style.removeProperty('--dp-lauf-weg');
    element.style.removeProperty('--dp-lauf-dauer');
  }
}

/*
 * Ein Beobachter fuer alle: Er sieht den Kasten (wird das Fenster schmaler)
 * und die Huelle (kommt die Schrift, wird der Text breiter). Beide Meldungen
 * fuehren zum Kasten zurueck.
 */
const beobachter =
  'ResizeObserver' in window
    ? new ResizeObserver((eintraege) => {
        const kaesten = new Set();
        for (const eintrag of eintraege) {
          const ziel = eintrag.target;
          kaesten.add(ziel.matches('[data-dp-lauf]') ? ziel.parentElement : ziel);
        }
        for (const kasten of kaesten) if (kasten instanceof HTMLElement) pruefe(kasten);
      })
    : null;

/*
 * Schieben von Hand. Der Name laesst sich seitlich schieben (overflow-x im
 * Stilblatt) - lief die Animation dabei weiter, addierten sich beide
 * Bewegungen, und der Name wanderte weit ueber sein Ende hinaus (Jimmi,
 * September 2026). Beim ersten Schieben uebergibt die Animation deshalb an
 * das Scrollen: der Versatz, an dem sie gerade steht, wird zur Scroll-
 * Position, die Animation ruht (data-dp-lauf-hand, animation: none direkt an
 * der Huelle) - es springt nichts.
 * Nach einer Weile ohne Schieben rollt der Name weich an den Anfang zurueck,
 * und die Animation beginnt dort wieder mit ihrer Pause.
 */
const HAND_RUHE = 6000;

/** @param {HTMLElement} element */
function schiebtVonHand(element) {
  if (element.dataset.dpLaufZurueck !== undefined) {
    // Das eigene Zurueckrollen meldet sich ebenfalls als Scrollen.
    if (element.scrollLeft <= 1) beendeHand(element);
    return;
  }
  if (element.dataset.dpLaufHand === undefined) {
    const huelle = element.querySelector(':scope > [data-dp-lauf]');
    let versatz = 0;
    if (huelle) {
      const matrix = getComputedStyle(huelle).transform;
      if (matrix && matrix !== 'none') versatz = new DOMMatrixReadOnly(matrix).m41;
    }
    element.dataset.dpLaufHand = '';
    // Direkt an der Huelle - so gilt es vor jeder Regel im Stilblatt.
    if (huelle) huelle.style.animation = 'none';
    if (versatz < 0) element.scrollLeft += -versatz;
  }
  clearTimeout(element.dpLaufUhr);
  element.dpLaufUhr = setTimeout(() => {
    if (element.scrollLeft <= 1) {
      beendeHand(element);
      return;
    }
    element.dataset.dpLaufZurueck = '';
    element.scrollTo({ left: 0, behavior: 'smooth' });
  }, HAND_RUHE);
}

/** @param {HTMLElement} element */
function beendeHand(element) {
  clearTimeout(element.dpLaufUhr);
  delete element.dataset.dpLaufZurueck;
  delete element.dataset.dpLaufHand;
  const huelle = element.querySelector(':scope > [data-dp-lauf]');
  if (huelle) huelle.style.removeProperty('animation');
}

/** @param {Element} element */
function richteEin(element) {
  if (!(element instanceof HTMLElement)) return;
  const huelle = huelleVon(element);
  if (element.dpLaufHandBereit !== true) {
    element.dpLaufHandBereit = true;
    element.addEventListener('scroll', () => schiebtVonHand(element), { passive: true });
    // Greift der Kunde waehrend des Zurueckrollens wieder zu, gilt wieder seine Hand.
    const zugriff = () => delete element.dataset.dpLaufZurueck;
    element.addEventListener('pointerdown', zugriff, { passive: true });
    element.addEventListener('wheel', zugriff, { passive: true });
  }
  if (!beobachter) {
    pruefe(element);
    return;
  }
  /* Beobachtet wird jeweils einmal; die erste Meldung liefert die erste Messung. */
  if (element.dpLaufKasten !== true) {
    element.dpLaufKasten = true;
    beobachter.observe(element);
  }
  if (huelle.dpLaufHuelle !== true) {
    huelle.dpLaufHuelle = true;
    beobachter.observe(huelle);
  }
}

function starte() {
  for (const element of document.querySelectorAll(AUSWAHL)) richteEin(element);
}

/*
 * Teile der Seite entstehen nachtraeglich - Kacheln aus Empfehlungen, Suche
 * oder Filtern, die Felder im Menue beim ersten Oeffnen. Statt das ganze
 * Dokument zu beobachten, tragen die Namen eine unmerkliche Animation
 * (dp-lauf-da im Stilblatt). Ihr Start meldet sich, sobald ein solcher Name
 * erscheint - auch wenn er von verborgen auf sichtbar wechselt. Sonst kostet
 * das nichts.
 */
document.addEventListener(
  'animationstart',
  (ereignis) => {
    if (ereignis.animationName !== 'dp-lauf-da') return;
    const ziel = ereignis.target;
    if (ziel instanceof Element && ziel.matches(AUSWAHL)) richteEin(ziel);
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte, { once: true });
} else {
  starte();
}

/*
 * Beim Variantenwechsel kann der Text ausgetauscht werden - dann fehlt die
 * Huelle. richteEin legt sie neu an; der Beobachter meldet sich daraufhin.
 */
document.addEventListener('variant:update', starte);
